import React, { useState, useEffect } from 'react';
import { logBehavior } from '../../Data/Behavior';
import { useSignIn } from '@/Recoil/Session/Session';

interface EmailOtpFlowProps {
    onSuccess: () => void;
    onBack?: () => void;
    initialEmail?: string;
    initialUserid?: string;
    initialOtp?: string;
    autoVerify?: boolean;
    showBackButton?: boolean;
}

const EmailOtpFlow: React.FC<EmailOtpFlowProps> = ({
    onSuccess,
    onBack,
    initialEmail = '',
    initialUserid = '',
    initialOtp = '',
    autoVerify = false,
    showBackButton = true
}) => {
    const { EmailSignupWithOtp, requestOtp } = useSignIn();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [step, setStep] = useState<'email' | 'otp'>('email');
    const [email, setEmail] = useState(initialEmail);
    const [userid,] = useState(initialUserid);
    const [otp, setOtp] = useState(initialOtp);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    useEffect(() => {
        if (initialOtp) {
            setStep('otp');
        }
    }, [initialOtp]);

    // Auto-verify OTP if all params are provided
    useEffect(() => {
        if (autoVerify && initialOtp && (initialEmail || initialUserid) && !isLoading) {
            handleVerifyOtp();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoVerify, initialOtp, initialEmail, initialUserid]);

    const handleSendOtp = async () => {
        if (!email && !userid) {
            setError('Please enter your email address or user ID');
            return;
        }

        try {
            setError(null);
            setIsLoading(true);

            // Request OTP through the API
            const otpResult = await requestOtp({ email, userid, notice_by: 'email' });

            if (otpResult.code === 200) {
                // Move to OTP verification step
                setStep('otp');
                setSuccessMessage('Verification code sent! Please check your email.');

                // Auto-clear success message after 3 seconds
                setTimeout(() => {
                    setSuccessMessage(null);
                }, 3000);
            } else {
                setError(otpResult.message || 'Failed to send verification code');
            }
        } catch (err) {
            setError('Failed to send verification code. Please try again.');
            console.error('Send OTP error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!otp) {
            setError('Please enter the verification code');
            return;
        }

        // Check we have either email or userid
        if (!email && !userid) {
            setError('Email or User ID is required for verification');
            return;
        }

        try {
            setError(null);
            setIsLoading(true);

            // Use EmailSignupWithOtp with a default name if not provided
            const result = await EmailSignupWithOtp(email, otp, 'User', userid);

            if (result && result.userid) {
                logBehavior('login_successful', { 
                    method: 'email_otp',
                    fromUrl: autoVerify
                });
                setSuccessMessage('Login successful! Redirecting...');
                
                // Call success callback
                setTimeout(() => {
                    onSuccess();
                }, 1000);
            } else {
                setError('Verification failed. Please try again.');
            }
        } catch (err) {
            setError('An error occurred during verification. Please try again.');
            console.error('Verify OTP error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    if (autoVerify && isLoading) {
        return (
            <div className="auto-verify-message">
                <p>{"Verifying your login..."}</p>
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <>
            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}

            <form>
                <div className="form-group">
                    <label htmlFor="email">{"Email address"}</label>
                    <input
                        type="email" 
                        id="email" 
                        disabled={isLoading || step !== 'email'}
                        autoComplete="email"
                        value={email} 
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email"
                        required
                    />
            
                    <label htmlFor="otp">{"Verification Code"}</label>
                    <input
                        type="text" 
                        id="otp" 
                        disabled={isLoading || step !== 'otp'}
                        value={otp} 
                        onChange={(e) => setOtp(e.target.value)}
                        placeholder="Enter verification code"
                        required
                    />
                </div>
                
                {step === 'email' && (
                    <button 
                        type="submit" 
                        className="submit-button" 
                        disabled={isLoading} 
                        onClick={(e) => { e.preventDefault(); handleSendOtp(); }}
                    >
                        {isLoading ? 'Sending...' : 'Send verification code'}
                    </button>
                )}
                
                {step === 'otp' && (
                    <button 
                        type="submit" 
                        className="submit-button" 
                        disabled={isLoading} 
                        onClick={(e) => { e.preventDefault(); handleVerifyOtp(); }}
                    >
                        {isLoading ? 'Verifying...' : 'Verify and Login'}
                    </button>
                )}
                
                {!autoVerify && step === 'otp' && (
                    <button
                        type="button" 
                        className="back-button"
                        onClick={() => setStep('email')} 
                        disabled={isLoading}
                    >
                        {"Back"}
                    </button>
                )}
                
                {step === 'email' && showBackButton && onBack && (
                    <button
                        type="button" 
                        className="back-button"
                        onClick={onBack} 
                        disabled={isLoading}
                    >
                        {"Back"}
                    </button>
                )}
            </form>
        </>
    );
};

export default EmailOtpFlow;